import { FormattedMessage, useIntl } from "react-intl";
import { FC, useRef, useState } from "react";
import { LuImageUp } from "react-icons/lu";
import { FiTrash } from "react-icons/fi";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import {
	useEditProductMutation,
	useUploadProductImagesMutation,
} from "../../../api/productsAPI";
import { TProductVariants } from "../../../api/api.types";

type TEditImagesModalProps = {
	onHide: () => void;
	product: TProductVariants;
};

const EditImagesModal: FC<TEditImagesModalProps> = ({ onHide, product }) => {
	const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);
	const [images, setImages] = useState<File[]>([]);

	const intl = useIntl();

	const inputRef = useRef<HTMLInputElement>(null);

	const [updateProduct, { isLoading: isUpdatingProduct }] =
		useEditProductMutation();
	const [uploadProductImages, { isLoading }] =
		useUploadProductImagesMutation();

	const handleDeleteSelectedImage = (index: number) => {
		setImages((prev) => prev.filter((_, i) => i !== index));
	};

	const handleAddImageToDelete = (id: number) => {
		setImagesToDelete((prev) => {
			return [...prev, id];
		});
	};

	const handleUploadImages = () => {
		if (inputRef.current) {
			inputRef.current.click();
		}
	};

	const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			const filesArray = Array.from(e.target.files);
			setImages((prev) => [...prev, ...filesArray]);
		}
	};

	const handleSave = async () => {
		if (!images.length && !imagesToDelete.length) {
			onHide();
			return;
		}

		const uploadedImages: number[] = [];

		if (images.length > 0) {
			const formData = new FormData();

			images.forEach((image) => {
				formData.append("images", image);
			});

			const response = await uploadProductImages({
				id: product.id,
				data: formData,
			});

			if (response.data?.image_ids) {
				uploadedImages.push(...response.data.image_ids);
			}
		}

		const filteredImages = product.images
			.filter((image) => !imagesToDelete.includes(image.id))
			.map((image) => image.id);
		const allImages = [...filteredImages, ...uploadedImages];

		updateProduct({
			id: product.id,
			data: {
				existing_images: allImages,
			},
		}).then((res) => {
			if ("error" in res) {
				const status = (res.error as { status: number }).status;

				if (status !== 403) {
					toast.error(
						intl.formatMessage({ id: "somethingWentWrong" })
					);
				}
			} else {
				toast.success(
					intl.formatMessage({
						id: "productImagesUpdatedSuccessfully",
					})
				);
				onHide();
			}
		});
	};

	return (
		<Modal className="modal-700" centered show={true} onHide={onHide}>
			<Modal.Header className="modal__header" closeButton>
				<Modal.Title className="modal__title">
					<FormattedMessage id="editImages" />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="pt-0 d-flex flex-column gap-4">
				<div className="edit-images__wrapper">
					{product.images.map((image) => {
						if (imagesToDelete.includes(image.id)) {
							return null;
						}

						return (
							<div
								onClick={() => handleAddImageToDelete(image.id)}
								className="edit-images__image"
							>
								<img
									src={image.image ? image.image : image.url}
									alt=""
								/>
								<FiTrash
									className="remove-icon"
									color="#d82323"
									strokeWidth={1.2}
									size={50}
								/>
							</div>
						);
					})}
					{images.map((image, index) => (
						<div
							onClick={() => handleDeleteSelectedImage(index)}
							className="edit-images__image"
						>
							<img src={URL.createObjectURL(image)} alt="" />
							<FiTrash
								className="remove-icon"
								color="#d82323"
								strokeWidth={1.2}
								size={50}
							/>
						</div>
					))}
					<div
						onClick={handleUploadImages}
						className="edit-images__image upload"
					>
						<LuImageUp
							color="#666666"
							strokeWidth={1.2}
							size={50}
						/>
					</div>
				</div>
				<div className="d-flex align-items-center gap-2">
					<button onClick={onHide} className="secondary-button w-100">
						<FormattedMessage id="cancel" />
					</button>
					<button
						disabled={isLoading || isUpdatingProduct}
						onClick={handleSave}
						className="ice-button w-100"
					>
						<FormattedMessage id="save" />
					</button>
				</div>
				<input
					onChange={handleSelectFiles}
					multiple
					accept="image/*"
					ref={inputRef}
					className="d-none"
					type="file"
				/>
			</Modal.Body>
		</Modal>
	);
};

export default EditImagesModal;
